import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Chip, SxProps, TextField, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { AllergenIcon } from "./AllergenIcon";

export interface AllergenOption {
  key: string;
}

interface AllergenPickerProps {
  options: readonly AllergenOption[];
  value: readonly string[];
  // Selected allergens that are shown but cannot be removed
  readOnlyValue?: readonly string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
  sx?: SxProps;
}

export const AllergenPicker = ({ options, value, readOnlyValue = [], onChange, disabled, sx }: AllergenPickerProps) => {
  const { t, i18n } = useTranslation();

  const getName = (option: AllergenOption) => t(`allergen.${option.key}.name`, { defaultValue: option.key });
  const byName = (a: AllergenOption, b: AllergenOption) => getName(a).localeCompare(getName(b), i18n.language);

  const sortedOptions = [...options].sort(byName);
  const selected = value.map(key => ({ key })).sort(byName);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disableClearable
      filterSelectedOptions
      disabled={disabled}
      options={sortedOptions}
      value={selected}
      sx={sx}
      isOptionEqualToValue={(option, selectedOption) => option.key === selectedOption.key}
      getOptionLabel={getName}
      onChange={(_, newValue) => onChange([...new Set([...readOnlyValue, ...newValue.map(option => option.key)])])}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              sx={{ height: 26, "&& .MuiChip-icon": { color: "text.primary", fontSize: 16, width: 16, height: 16 } }}
              icon={<AllergenIcon allergenKey={option.key} size="small" />}
              deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onDelete={disabled || readOnlyValue.includes(option.key) ? undefined : onDelete}
              label={getName(option)}
            />
          );
        })
      }
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props} sx={{ gap: 1.5 }}>
          <AllergenIcon allergenKey={option.key} sx={{ fontSize: 24 }} />
          <Box>
            <Typography variant="body2">{getName(option)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t(`allergen.${option.key}.description`, { defaultValue: "" })}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={t("allergenPicker.label")}
          inputProps={{ ...params.inputProps, placeholder: value.length ? "" : t("allergenPicker.placeholder") }}
        />
      )}
    />
  );
};
